// Analytics tracking utilities for Redline Monitoring Services Inc.
// Comprehensive tracking for Virtual Guard business metrics

// Google Analytics 4 event tracking
export const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      event_category: parameters.category || 'engagement',
      event_label: parameters.label || '',
      value: parameters.value || 0,
      custom_parameter_1: parameters.hoa_community_type || '',
      custom_parameter_2: parameters.referral_source || '',
      custom_parameter_3: parameters.service_interest || '',
      ...parameters
    });
  }

  // Also send to Google Tag Manager dataLayer
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      eventCategory: parameters.category || 'engagement',
      eventAction: parameters.action || eventName,
      eventLabel: parameters.label || '',
      eventValue: parameters.value || 0,
      customParameters: {
        hoaCommunityType: parameters.hoa_community_type || '',
        referralSource: parameters.referral_source || '',
        serviceInterest: parameters.service_interest || ''
      },
      ...parameters
    });
  }
};

// Lead Generation Tracking
export const trackConsultationRequest = (leadData) => {
  trackEvent('consultation_request', {
    category: 'lead_generation',
    action: 'form_submission',
    label: 'virtual_guard_consultation',
    value: 100, // Assign value to consultation requests
    hoa_community_type: leadData.community || 'unspecified',
    referral_source: leadData.referralSource || 'direct',
    service_interest: 'virtual_guards',
    lead_type: 'consultation',
    contact_method: 'form'
  });
};

export const trackNewsletterSignup = (email) => {
  trackEvent('newsletter_signup', {
    category: 'lead_generation',
    action: 'email_signup',
    label: 'security_newsletter',
    value: 25, // Assign value to newsletter signups
    lead_type: 'newsletter',
    contact_method: 'email'
  });
};

// Phone & Contact Tracking
export const trackPhoneClick = (location) => {
  trackEvent('phone_click', {
    category: 'contact',
    action: 'phone_click',
    label: `phone_${location}`,
    value: 50,
    contact_method: 'phone',
    click_location: location
  });
};

export const trackEmailClick = (location) => {
  trackEvent('email_click', {
    category: 'contact',
    action: 'email_click',
    label: `email_${location}`,
    value: 30,
    contact_method: 'email',
    click_location: location
  });
};

// Service Interest Tracking
export const trackServiceInterest = (serviceName, action = 'view') => {
  trackEvent('service_interest', {
    category: 'services',
    action: action,
    label: serviceName.toLowerCase().replace(/\s+/g, '_'),
    service_interest: serviceName,
    engagement_level: action
  });
};

// Page View Tracking with Custom Dimensions
export const trackPageView = (pageName, customData = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-NNNQ6GSQRS', {
      page_title: pageName,
      page_location: window.location.href,
      custom_map: {
        'custom_parameter_1': customData.hoa_community_type || '',
        'custom_parameter_2': customData.referral_source || '',
        'custom_parameter_3': customData.service_interest || ''
      }
    });
  }
};

// First-party visit tracking (sends to our own backend for the admin analytics panel)
export const trackVisit = (pageName = "") => {
  try {
    if (typeof window !== "undefined" && window.self !== window.top) return; // skip inside iframes (e.g. admin heatmap preview)
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    const url = new URL(window.location.href);
    let vid = localStorage.getItem("rl_visitor_id");
    if (!vid) {
      vid = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("rl_visitor_id", vid);
    }
    const ua = navigator.userAgent || "";
    const device = /iPad|Tablet/i.test(ua)
      ? "Tablet"
      : /Mobi|Android|iPhone/i.test(ua)
        ? "Mobile"
        : "Desktop";
    fetch(`${backend}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        path: url.pathname,
        page: pageName,
        referrer: document.referrer || "",
        utm_source: url.searchParams.get("utm_source") || "",
        utm_medium: url.searchParams.get("utm_medium") || "",
        utm_campaign: url.searchParams.get("utm_campaign") || "",
        visitor_id: vid,
        device,
      }),
    }).catch(() => {});
  } catch (e) {
    // no-op
  }
};

// First-party click tracking for the admin heatmap
export const trackClick = (e) => {
  try {
    if (typeof window !== "undefined" && window.self !== window.top) return; // skip inside iframes (heatmap preview)
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    const docW = document.documentElement.scrollWidth || window.innerWidth || 1;
    const docH = document.documentElement.scrollHeight || 1;
    const x = e.pageX / docW;
    const y = e.pageY / docH;
    if (!(x >= 0 && x <= 1 && y >= 0 && y <= 1)) return;
    fetch(`${backend}/api/track/click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        path: window.location.pathname,
        x_pct: x,
        y_pct: y,
        vw: window.innerWidth,
      }),
    }).catch(() => {});
  } catch (err) {
    // no-op
  }
};

// Conversion Tracking
export const trackConversion = (conversionType, value = 0) => {
  trackEvent('conversion', {
    category: 'conversions',
    action: 'completed',
    label: conversionType,
    value: value,
    conversion_type: conversionType
  });
};

// Virtual Guard Specific Metrics
export const trackVirtualGuardInterest = (communityType, action) => {
  trackEvent('virtual_guard_interest', {
    category: 'virtual_guards',
    action: action,
    label: communityType,
    hoa_community_type: communityType,
    service_interest: 'dedicated_virtual_guards'
  });
};

// HOA Community Type Tracking
export const trackHOACommunityType = (communityType) => {
  trackEvent('hoa_community_type', {
    category: 'demographics',
    action: 'community_type_interest',
    label: communityType,
    hoa_community_type: communityType
  });
};

// User Engagement Tracking
export const trackScrollDepth = (percentage) => {
  trackEvent('scroll_depth', {
    category: 'engagement',
    action: 'scroll',
    label: `${percentage}%`,
    value: percentage
  });
};

export const trackTimeOnPage = (seconds) => {
  trackEvent('time_on_page', {
    category: 'engagement',
    action: 'time_spent',
    label: 'page_duration',
    value: seconds
  });
};

// Social Media & Referral Tracking
export const trackSocialShare = (platform, url) => {
  trackEvent('social_share', {
    category: 'social',
    action: 'share',
    label: platform,
    share_platform: platform,
    shared_url: url
  });
};

export const trackReferralSource = (source, medium = '', campaign = '') => {
  trackEvent('referral_tracking', {
    category: 'acquisition',
    action: 'referral',
    label: source,
    referral_source: source,
    referral_medium: medium,
    referral_campaign: campaign
  });
};

// Error & Performance Tracking
export const trackError = (errorType, errorMessage) => {
  trackEvent('error', {
    category: 'errors',
    action: errorType,
    label: errorMessage,
    error_type: errorType,
    error_message: errorMessage
  });
};

export const trackPerformance = (metric, value) => {
  trackEvent('performance', {
    category: 'site_performance',
    action: metric,
    label: `${metric}_timing`,
    value: Math.round(value),
    performance_metric: metric
  });
};

// Custom Business Metrics for Virtual Security
export const trackSecurityServiceInquiry = (serviceType, inquiryMethod) => {
  trackEvent('security_service_inquiry', {
    category: 'business_metrics',
    action: 'service_inquiry',
    label: serviceType,
    value: 75,
    service_interest: serviceType,
    inquiry_method: inquiryMethod,
    business_category: 'virtual_security'
  });
};

export default {
  trackEvent,
  trackConsultationRequest,
  trackNewsletterSignup,
  trackPhoneClick,
  trackEmailClick,
  trackServiceInterest,
  trackPageView,
  trackVisit,
  trackConversion,
  trackVirtualGuardInterest,
  trackHOACommunityType,
  trackScrollDepth,
  trackTimeOnPage,
  trackSocialShare,
  trackReferralSource,
  trackError,
  trackPerformance,
  trackSecurityServiceInquiry
};